import {
  CrewMemberTacho,
  LegalCounter,
  LegalCounters,
  WeeklyRestRecord,
} from "@/reducers/tachosSlice"

// GPS ships the legal-counter panel and the crew members as JSON attributes
// of the tacho item (snake_case, pydantic dumps). Anything malformed reads
// as "no panel" rather than breaking the tacho itself.

type Json = Record<string, unknown>

export const asObject = (value: unknown): Json | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Json)
    : null

const asInt = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : null

export const asString = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null

const asBool = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null

const parseCounter = (value: unknown): LegalCounter | null => {
  const item = asObject(value)
  if (!item) return null
  return {
    total: asInt(item.total),
    limit: asInt(item.limit),
    remaining: asInt(item.remaining),
  }
}

const parseWeeklyRest = (value: unknown): WeeklyRestRecord | null => {
  const item = asObject(value)
  const weeksAgo = item ? asInt(item.weeks_ago) : null
  if (!item || weeksAgo === null) return null
  return {
    weeksAgo,
    total: asInt(item.total),
    limit: asInt(item.limit),
    remaining: asInt(item.remaining),
    country: asString(item.country),
    atHome: asBool(item.at_home),
    compensationDeadline: asString(item.compensation_deadline),
  }
}

export const parseJson = (raw: string | null | undefined): unknown => {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const legalCountersFromJson = (value: unknown): LegalCounters | null => {
  const item = asObject(value)
  if (!item) return null
  const weeklyRests = Array.isArray(item.weekly_rests)
    ? item.weekly_rests
        .map(parseWeeklyRest)
        .filter((rest): rest is WeeklyRestRecord => rest !== null)
    : []
  return {
    continuousDriving: parseCounter(item.continuous_driving),
    dailyDriving: parseCounter(item.daily_driving),
    dailyAmplitude: parseCounter(item.daily_amplitude),
    extendedDrivingDays: parseCounter(item.extended_driving_days),
    reducedDailyRestDays: parseCounter(item.reduced_daily_rest_days),
    weeklyDriving: parseCounter(item.weekly_driving),
    biweeklyDriving: parseCounter(item.biweekly_driving),
    consecutiveDrivingDays: parseCounter(item.consecutive_driving_days),
    workWeek: parseCounter(item.work_week),
    workWeekEnd: asString(item.work_week_end),
    middayBreak: parseCounter(item.midday_break),
    dailyRest: parseCounter(item.daily_rest),
    previousDailyRest: parseCounter(item.previous_daily_rest),
    weeklyRestCompensation: parseCounter(item.weekly_rest_compensation),
    weeklyRests,
  }
}

export const parseLegalCounters = (
  raw: string | null | undefined,
): LegalCounters | null => legalCountersFromJson(parseJson(raw))

export const parseCrewMembers = (
  raw: string | null | undefined,
): CrewMemberTacho[] | undefined => {
  const entries = parseJson(raw)
  if (!Array.isArray(entries)) return undefined
  const members = entries
    .map(asObject)
    .filter((entry): entry is Json => entry !== null)
    .map((entry) => ({
      driverId: asString(entry.driver_id),
      driverName: asString(entry.driver_name),
      legalCounters: legalCountersFromJson(entry.legal_counters),
    }))
  return members.length > 0 ? members : undefined
}

// True when the tacho, or any member of its crew, carries a panel to show.
export const hasLegalCounters = (
  legalCounters: LegalCounters | null | undefined,
  crew: CrewMemberTacho[] | undefined,
): boolean =>
  Boolean(legalCounters) ||
  Boolean(crew?.some((member) => member.legalCounters))
